import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import MedicalDocumentsPage from './pages/MedicalDocumentsPage';
import AdministrativeDocumentsPage from './pages/AdministrativeDocumentsPage';
import ProtectedRoute from './ProtectedRoute';
import UserManagementPage from './pages/UserManagementPage';
import UserRegisterPage from './components/Users/UserRegisterPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SendDocumentPage from './pages/SendDocumentPage';
import ReportsPage from './pages/ReportsPage';
import PatientAccessPage from './pages/PatientAccessPage';
import SelfieCapturePage from './pages/SelfieCapturePage';
import PlantillaEditorPage from './pages/PlantillaEditorPage';
import PlantillasPage from './pages/PlantillasPage';
import DocumentViewPage from './pages/DocumentViewPage';
import SignatureCapturePage from './pages/SignatureCapturePage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import 'bootstrap-icons/font/bootstrap-icons.css';
import AdminSendDocumentPage from './pages/AdminSendDocumentPage';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * APP.JS - BIOFIRMAS CTX
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Las rutas ahora usan PERMISOS en lugar de ROLES.
 * Esto significa que cuando crees un nuevo rol, automáticamente tendrá acceso
 * a las páginas según los permisos que le asignes en la matriz.
 * 
 * NO necesitas modificar este archivo cuando crees nuevos roles.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

// Componente detector de rutas (debug)
function RouteErrorBoundary() {
  const location = useLocation();
  
  useEffect(() => {
    console.log('🌐 RUTA ACTUAL:', location.pathname);
    console.log('📦 STATE:', location.state);
    console.log('---');
  }, [location]);
  
  return null;
}

function App() {
  useEffect(() => {
    // Gestión de seguridad de sesión
    
    const handleBeforeUnload = (event) => {
      localStorage.clear();
      event.preventDefault();
      event.returnValue = '¿Estás seguro de que quieres salir? Tu sesión se cerrará.';
      
      navigator.sendBeacon('/api/logout', JSON.stringify({ 
        action: 'window_close' 
      }));
    };

    let inactivityTimer;
    let warningTimer;
    const INACTIVITY_TIME = 90 * 60 * 1000;
    const WARNING_TIME = 85 * 60 * 1000;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      
      inactivityTimer = setTimeout(() => {
        alert('Tu sesión ha expirado por inactividad (90 minutos). Serás redirigido al login.');
        localStorage.clear();
        window.location.href = 'https://biofirmabk.colombianadetrasplantes.com/auth/logout';
      }, INACTIVITY_TIME);

      warningTimer = setTimeout(() => {
        const confirmStay = window.confirm(
          'Tu sesión expirará en 5 minutos por inactividad. ¿Quieres mantener la sesión activa?'
        );
        if (confirmStay) resetTimer();
      }, WARNING_TIME);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown', 'touchmove'];
    const handleActivity = () => resetTimer();

    const hasActiveSession = localStorage.getItem('token') || 
                            localStorage.getItem('nombres') || 
                            localStorage.getItem('correo');

    if (hasActiveSession) {
      console.log('🔒 Gestión de seguridad de sesión activada');
      window.addEventListener('beforeunload', handleBeforeUnload);
      activityEvents.forEach(event => document.addEventListener(event, handleActivity, true));
      resetTimer();
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      activityEvents.forEach(event => document.removeEventListener(event, handleActivity, true));
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
    };
  }, []);

  return (
    <Router>
      <RouteErrorBoundary />
      <Routes>
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* RUTAS PÚBLICAS */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* DASHBOARD - Cualquier usuario autenticado */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* DOCUMENTOS MÉDICOS - Requiere permiso verDocumentosMedicos */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/medical-documents"
          element={
            <ProtectedRoute requiredPermissions={['verDocumentosMedicos']}>
              <MedicalDocumentsPage />
            </ProtectedRoute>
          }
        />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* DOCUMENTOS ADMINISTRATIVOS - Requiere permiso verDocumentosAdministrativos */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/administrative-documents"
          element={
            <ProtectedRoute requiredPermissions={['verDocumentosAdministrativos']}>
              <AdministrativeDocumentsPage />
            </ProtectedRoute>
          }
        />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ENVIAR DOCUMENTO - Requiere algún permiso de envío */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/send-document"
          element={
            <ProtectedRoute requiredPermissions={['enviarDocumentosMedicos', 'enviarDocumentosAdministrativos', 'enviarDocumentosFarmacia']}>
              <SendDocumentPage />
            </ProtectedRoute>
          }
        />
        
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* REPORTES - Requiere permiso verReportes */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredPermissions={['verReportes']}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* PLANTILLAS - Requiere permiso gestionarPlantillas */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/plantillas"
          element={
            <ProtectedRoute requiredPermissions={['gestionarPlantillas']}>
              <PlantillasPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/plantillas/:id/editor"
          element={
            <ProtectedRoute requiredPermissions={['gestionarPlantillas']}>
              <PlantillaEditorPage />
            </ProtectedRoute>
          }
        />
        
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* GESTIÓN DE USUARIOS - Requiere permiso gestionarUsuarios */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/register"
          element={
            <ProtectedRoute requiredPermissions={['gestionarUsuarios']}>
              <UserRegisterPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/user-management"
          element={
            <ProtectedRoute requiredPermissions={['gestionarUsuarios']}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* CONFIGURACIÓN - Requiere permiso configurarSistema */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route
          path="/configuracion"
          element={
            <ProtectedRoute requiredPermissions={['configurarSistema']}>
              <ConfiguracionPage />
            </ProtectedRoute>
          }
        />

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* RUTAS PÚBLICAS PARA PACIENTES */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/patient-access" element={<PatientAccessPage />} />
        <Route path="/selfie-capture/:cedula" element={<SelfieCapturePage />} />
        <Route path="/document-view/:cedula" element={<DocumentViewPage />} />
        <Route path="/firmar/:id" element={<SignatureCapturePage />} />
        <Route path="/ver-documento" element={<DocumentViewPage />} />
        <Route path='/send-admin-document' element={<ProtectedRoute requiredPermissions={['enviarDocumentosAdministrativos']}>
    <AdminSendDocumentPage />
  </ProtectedRoute>
} />
        
      </Routes>
    </Router>
  );
}

export default App;